import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {ViewerMode} from '@puppycad/cad-core';

/**
 * 判断指定模式是否为空闲态模式。
 *
 * @param mode 当前 Viewer 模式。
 * @returns 是否属于非草图、非命令态的空闲模式。
 */
export function isIdleViewerMode(mode: ViewerMode): boolean {
    return mode === ViewerMode.Default || mode === ViewerMode.Assembly;
}

/**
 * 草图状态全局 Store。
 * 职责：
 * 1. 复用 Viewer 提供的唯一模式枚举；
 * 2. 管理当前草图会话 ID 与待执行草图命令；
 * 3. 提供草图是否激活、是否处于绘制中的派生状态。
 */
export const useSketchStateStore = defineStore('sketchState', () => {
    /**
     * 判断是否为草图相关模式。
     * @param mode Viewer 模式。
     * @returns 是否为草图相关模式。
     */
    function isSketchViewerMode(mode: ViewerMode): boolean {
        return mode === ViewerMode.Sketch
            || mode === ViewerMode.SketchDrawing
            || mode === ViewerMode.SketchTrim
            // 智能尺寸命令族在草图会话内运行：缺失该模式会让工具栏/命令链整体退化为非草图态。
            || mode === ViewerMode.SketchDimension
            || mode === ViewerMode.SketchMoveEntities
            || mode === ViewerMode.SketchCopyEntities
            || mode === ViewerMode.SketchRotateEntities
            || mode === ViewerMode.SketchCircularPattern
            || mode === ViewerMode.SketchMirrorEntities
            || mode === ViewerMode.SketchOffset;
    }

    /** 当前 Viewer 模式。 */
    const viewerMode = ref<ViewerMode>(ViewerMode.Default);

    /** 草图是否处于激活状态。 */
    const isSketchActive = computed(() => isSketchViewerMode(viewerMode.value));

    /** 草图是否处于绘制状态。 */
    const isSketchDrawing = computed(() => viewerMode.value === ViewerMode.SketchDrawing);

    /** 当前编辑中的草图组 ID。 */
    const currentSketchGroupId = ref<string | null>(null);

    /** 待执行的草图命令。 */
    const pendingSketchToolCommand = ref<string | null>(null);

    /**
     * 设置当前 Viewer 模式。
     * @param mode 目标模式。
     */
    function setViewerMode(mode: ViewerMode): void {
        viewerMode.value = mode;
    }

    /**
     * 清除待执行草图命令。
     */
    function clearPendingSketchToolCommand(): void {
        pendingSketchToolCommand.value = null;
    }

    /**
     * 重置草图运行时会话状态。
     *
     * @param mode 重置后回落的 Viewer 模式。
     */
    function resetSketchRuntimeState(mode: ViewerMode = ViewerMode.Default): void {
        // 新项目或新 Viewer 不能复用旧草图会话 ID，否则草图入口会被误判为“正在编辑草图”。
        viewerMode.value = mode;
        currentSketchGroupId.value = null;
        pendingSketchToolCommand.value = null;
    }

    return {
        viewerMode,
        isSketchActive,
        isSketchDrawing,
        currentSketchGroupId,
        pendingSketchToolCommand,
        setViewerMode,
        clearPendingSketchToolCommand,
        resetSketchRuntimeState,
    };
});
